import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Sanitizer } from '@angular/core';
import { combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { NextcloudFile, NotificationTask, PaginationPage, PaginationResponse } from 'src/app/classes';
import { serverLocation } from 'src/app/globals';
import { FileService } from '../../nextcloud/services/file.service';
import { Guid } from '../../shared/services/persistence.service';
import { TokenService } from '../../shared/services/token.service';
import { EMailAddress, EMailAudit, EMailInbox } from '../classes';

@Injectable({
  providedIn: 'root'
})
export class MailService {

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService,
    private _fileService: FileService
  ) { }

  downloadMailAttachement = (eMailId: string, eMailAccount: number, fileName: string): Observable<Blob> => this._httpClient.post(`${serverLocation}/api/mail/${eMailAccount === EMailInbox.Assignment ? 'assignment' : 'incident'}/attachement`, {
    mailId: eMailId,
    fileName: fileName
  }, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`),
    responseType: 'blob'
  });

  downloadEmbeddedFileByFileName = (inbox: number, messageId: string, fileName: string): Observable<Blob> => this._httpClient.post(`${serverLocation}/api/mail/inline-file`, {
    inbox: inbox,
    messageId: messageId,
    fileName: fileName
  }, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`),
    responseType: 'blob'
  });

  linkMailToIssue = (mailId: string, issueId: number): Observable<Object> => this._httpClient.get(`${serverLocation}/api/mail/${mailId}/${issueId}`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  });

  loadMailAddress(searchString: string = null, page: PaginationPage = { pageNumber: 1, pageSize: 20 }): Observable<PaginationResponse<EMailAddress[]>> {
    return this._httpClient.post(`${serverLocation}/api/mail-addresses`, {
      'searchString': searchString,
      'page': page
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<EMailAddress[]>>;
  }

  loadUnseenMailsFromMailInbox(account: 'incident' | 'assignment'): Observable<EMailAudit[]> {
    return this._httpClient.get(`${serverLocation}/api/mail/${account}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<EMailAudit[]>;
  }

  loadMailsFromMailInbox(account: 'incident' | 'assignment', paginationPage: PaginationPage = { 'pageNumber': 1, 'pageSize': 5 }): Observable<PaginationResponse<EMailAudit[]>> {
    return this._httpClient.post(`${serverLocation}/api/mail/${account}`, paginationPage, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<EMailAudit[]>>;
  }

  markAssignmentMailAsSeen(messageId: string): Observable<Object> {
    return this._httpClient.get(`${serverLocation}/api/mail/assignment/${messageId}/seen`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  markAssignmentMailAsNotSeen(messageId: string): Observable<Object> {
    return this._httpClient.get(`${serverLocation}/api/mail/assignment/${messageId}/not-seen`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  markIncidentMailAsSeen(messageId: string): Observable<Object> {
    return this._httpClient.get(`${serverLocation}/api/mail/incident/${messageId}/seen`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  markIncidentMailAsNotSeen(messageId: string): Observable<Object> {
    return this._httpClient.get(`${serverLocation}/api/mail/incident/${messageId}/not-seen`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  renderMsgAsHtml(blob: Blob, fileName: string): Observable<string> {
    let data = new FormData();
    data.append('file', blob, fileName);
    return this._httpClient.post(`${serverLocation}/api/mail/render-msg-file`, data, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<string>;
  }

  resolveEMailCids(mail: EMailAudit, cancellationObs: Observable<Object>): Observable<boolean> {
    if (!mail || !mail.htmlBody) return of(false);
    let cids = MailService.getCids(mail.htmlBody);
    if (cids.length === 0) return of(false);
    let obsMap = {};
    cids.forEach(x => obsMap[x] = new ReplaySubject<boolean>(1));
    combineLatest(
      cids.map(x => this.downloadEmbeddedFileByFileName(mail.inbox, mail.messageId, x).pipe(takeUntil(cancellationObs), catchError(x => of(null)))) as Observable<Blob>[]
    ).subscribe(
      (blobs: Blob[]) => {
        for (let index = 0; index < blobs.length; index++) {
          if (!blobs[index]) {
            obsMap[cids[index]].next(false);
            continue;
          }
          var a = new FileReader();
          a.onload = (result) => {
            let cid = cids[index];
            mail.htmlBody = mail.htmlBody.replace(`cid:${cid}`, result.target.result as string);
            obsMap[cids[index]].next(true);
          }
          a.readAsDataURL(blobs[index]);
        }
      }
    )
    return combineLatest(Object.keys(obsMap).map(x => obsMap[x])).pipe(take(1), map(x => true));
  }

  sendInternalNotification(task: NotificationTask, receiver: string): Observable<any> {
    return this._httpClient.post("api/notification", {
      notificationsubject: task.subject,
      notificationtext: task.content,
      notificationuser: receiver
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<any>;
  }

  sendMail(task: NotificationTask, receiver: string[]): Observable<any> {
    debugger;
    let subject = new Subject<any>();
    let callback = (form: FormData) => {
      this
        ._httpClient
        .post(`${serverLocation}/api/mail`, form, {
          headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
        })
        .subscribe(
          (result) => subject.next(result),
          (error) => subject.error(error),
          () => subject.complete()
        )
    }
    let extractedImages = MailService.extractImagesToBlobs(task.content);
    let formData = extractedImages.formData;
    formData.append("mail", new Blob([JSON.stringify({
      body: extractedImages.body,
      subject: task.subject,
      receiver: receiver
    })], {
      type: "application/json"
    }));
    if (typeof task.issueId === 'number') formData.append('issueId', task.issueId.toString());
    if (!Array.isArray(task.attachements) || task.attachements.length === 0) callback(formData);
    var resolved = 0;
    for (let attachement of task.attachements) {
      if (attachement.isNextcloudFile) {
        this._fileService.getFileByPath((attachement as NextcloudFile).href).subscribe(
          (blob: Blob) => {
            formData.append(FileService.getFileName((attachement as NextcloudFile).href), blob, FileService.getFileName((attachement as NextcloudFile).href));
            resolved++;
            if (resolved === (task as NotificationTask).attachements.length) callback(formData);
          },
          (error) => {
            subject.error(error);
          }
        );
      } else {
        console.warn("don't know how to handle file, drop it ...", attachement);
        resolved++;
        if (resolved === (task as NotificationTask).attachements.length) callback(formData);
      }
    }
    return subject;
  }

  static extractImagesToBlobs(body: string): EMailImagesToBlobModel {
    if (typeof (body) !== 'string') return {
      body: '',
      formData: new FormData()
    } as EMailImagesToBlobModel;
    let formData: FormData = new FormData();
    var guid;
    let regExp = /\<img *src=\"(.*?)\">/g;
    var match;
    var replaceIntervals = [];
    while ((match = regExp.exec(body)) != null) {
      guid = Guid.newGuid();
      let srcInner = /data:(.*?);/.exec(match[0]);
      let mediaType = srcInner[1];
      let subMediaType = mediaType.split("/")[1];
      let fullName = guid + "." + subMediaType;
      let dataArray = (match[1] as string).split(",");
      let base64 = dataArray[1];
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mediaType });
      formData.append(fullName, blob, fullName);
      replaceIntervals.push({ from: (match.index + srcInner.index), length: match[1].length, cid: fullName });
    }
    var cleared: string = "";
    var last = 0;
    for (let interval of replaceIntervals) {
      cleared += body.substr(last, (interval.from - last)) + "cid:" + interval.cid;
      last = interval.from + interval.length;
    }
    cleared += body.substr(last, body.length);
    return {
      body: cleared,
      formData: formData
    } as EMailImagesToBlobModel;
  }

  static getCids(body: string): string[] {
    let regex = /src=\"cid:(.*?)\"/gm;
    var cids: string[] = [];
    var result = regex.exec(body);
    while (result) {
      cids.push(result[1]);
      result = regex.exec(body);
    }
    return cids;
  }
}

export class EMailImagesToBlobModel {
  body: string;
  formData: FormData;
}
